import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import { 
  MapPin, Star, Clock, Phone, Mail, Globe as GlobeIcon, 
  Award, ChevronLeft, Calendar, Users, CheckCircle, 
  X, ArrowRight, Languages, GraduationCap, Video, User
} from 'lucide-react';
import 'leaflet/dist/leaflet.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Fix Leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

function CoachDetailPage() {
  const { coachId } = useParams();
  const navigate = useNavigate();
  const [coach, setCoach] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState([]);
  
  const [bookingForm, setBookingForm] = useState({
    booking_type: 'single_session',
    session_date: '',
    session_duration_hours: 1,
    sport: '',
    skill_level: 'intermediate',
    location_type: 'in_person',
    location_address: '',
    num_people: 1,
    package_id: '',
    special_requests: ''
  });

  useEffect(() => {
    fetchCoachDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coachId]);

  const fetchCoachDetails = async () => {
    try {
      setLoading(true);
      const [coachRes, reviewsRes] = await Promise.all([
        axios.get(`${API}/synergies/coaches/${coachId}`),
        axios.get(`${API}/synergies/coaches/${coachId}/reviews`).catch(() => ({ data: [] }))
      ]);
      setCoach(coachRes.data);
      setReviews(reviewsRes.data);
      
      // Set first specialization as default sport
      if (coachRes.data.specializations?.length > 0) {
        setBookingForm(prev => ({ ...prev, sport: coachRes.data.specializations[0] }));
      }
    } catch (error) {
      console.error('Error fetching coach details:', error);
      if (error.response?.status === 404) {
        navigate('/synergies/coaches');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('session_token');
    
    if (!token) {
      alert('Please login to book a coaching session');
      return;
    }

    try {
      const response = await axios.post(
        `${API}/synergies/coaches/${coachId}/book`,
        bookingForm,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.data.payment_url) {
        window.location.href = response.data.payment_url;
      }
    } catch (error) {
      console.error('Error creating booking:', error);
      alert(error.response?.data?.detail || 'Failed to create booking');
    }
  };

  const calculatePrice = () => {
    if (!coach) return 0;
    
    if (bookingForm.booking_type === 'package' && bookingForm.package_id) {
      const pkg = coach.session_packages?.find(p => p.id === bookingForm.package_id);
      return pkg?.price || 0;
    }
    
    return coach.hourly_rate * bookingForm.session_duration_hours;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-deep-dark flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-3 border-premium-gold/20 border-t-premium-gold"></div>
      </div>
    );
  }

  if (!coach) {
    return (
      <div className="min-h-screen bg-deep-dark flex items-center justify-center">
        <div className="text-center">
          <Award className="w-16 h-16 text-white/30 mx-auto mb-4" strokeWidth={1.5} />
          <h2 className="text-2xl font-bold text-white mb-2">Coach Not Found</h2>
          <Link to="/synergies/coaches" className="text-premium-gold hover:text-premium-gold-light">
            Back to Coaches
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-deep-dark">
      {/* Header */}
      <div className="bg-dark-surface border-b border-premium-gold/30/50 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/synergies/coaches"
            className="inline-flex items-center gap-2 text-white/60 hover:text-white mb-4 transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
            <span>Back to Coaches</span>
          </Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Profile Header */}
        <div className="bg-dark-surface rounded-xl p-8 border border-premium-gold/30/50 mb-8">
          <div className="flex flex-col md:flex-row gap-8">
            <div className="w-48 h-48 rounded-xl overflow-hidden flex-shrink-0">
              {coach.photo ? (
                <img 
                  src={coach.photo} 
                  alt={coach.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-imperial to-dark-purple">
                  <Award className="w-24 h-24 text-white/20" strokeWidth={1.5} />
                </div>
              )}
            </div>
            
            <div className="flex-1">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h1 
                    className="text-4xl md:text-5xl font-bold text-white mb-4"
                    style={{ fontFamily: 'Fraunces, serif' }}
                  >
                    {coach.first_name && coach.last_name 
                      ? `${coach.first_name} ${coach.last_name}`
                      : coach.name}
                  </h1>
                  <div className="flex flex-wrap items-center gap-3 mb-3">
                    {coach.verified && (
                      <span className="px-3 py-1 bg-premium-gold/20 border border-premium-gold text-premium-gold text-xs font-bold rounded-full">
                        Verified Coach
                      </span>
                    )}
                    {coach.featured && (
                      <span className="px-3 py-1 bg-premium-gold text-deep-dark text-xs font-bold rounded-full">
                        Featured
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-4 text-white/60 mb-4">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-5 h-5" strokeWidth={1.5} />
                      <span>{coach.city}, {coach.country}</span>
                    </div>
                    {coach.rating > 0 && (
                      <div className="flex items-center gap-2">
                        <Star className="w-5 h-5 text-premium-gold fill-current" strokeWidth={1.5} />
                        <span className="text-white font-semibold">{coach.rating.toFixed(1)}</span>
                        <span>({coach.review_count} reviews)</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <GraduationCap className="w-5 h-5" />
                      <span>{coach.experience_years} years experience</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <p className="text-white/70 leading-relaxed mb-4">{coach.bio}</p>
              
              <div className="flex flex-wrap gap-2">
                {coach.online_coaching && (
                  <span className="px-3 py-1 bg-deep-dark text-premium-gold text-sm rounded flex items-center gap-1">
                    <Video className="w-4 h-4" strokeWidth={1.5} /> Online Coaching
                  </span>
                )}
                {coach.in_person_coaching && (
                  <span className="px-3 py-1 bg-deep-dark text-premium-gold text-sm rounded flex items-center gap-1">
                    <User className="w-4 h-4" /> In-Person
                  </span>
                )}
                <span className="px-3 py-1 bg-deep-dark text-white/70 text-sm rounded">
                  {coach.total_sessions} Sessions Completed
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Specializations */}
            {coach.specializations?.length > 0 && (
              <div className="bg-dark-surface rounded-xl p-6 border border-premium-gold/30/50">
                <h2 className="text-xl font-bold text-white mb-4 uppercase" style={{ fontFamily: 'Fraunces, serif' }}>
                  Specializations
                </h2>
                <div className="flex flex-wrap gap-2">
                  {coach.specializations.map((spec, idx) => (
                    <span 
                      key={idx}
                      className="px-4 py-2 bg-deep-dark text-white text-sm rounded-lg capitalize"
                    >
                      {spec}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Skill Levels */}
            {coach.skill_levels?.length > 0 && (
              <div className="bg-dark-surface rounded-xl p-6 border border-premium-gold/30/50">
                <h2 className="text-xl font-bold text-white mb-4 uppercase" style={{ fontFamily: 'Fraunces, serif' }}>
                  Skill Levels Taught
                </h2>
                <div className="flex flex-wrap gap-2">
                  {coach.skill_levels.map((level, idx) => (
                    <span 
                      key={idx}
                      className="px-4 py-2 bg-deep-dark text-premium-gold text-sm rounded-lg capitalize font-semibold"
                    >
                      {level}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Certifications */}
            {coach.certifications?.length > 0 && (
              <div className="bg-dark-surface rounded-xl p-6 border border-premium-gold/30/50">
                <h2 className="text-xl font-bold text-white mb-4 uppercase" style={{ fontFamily: 'Fraunces, serif' }}>
                  Certifications & Credentials
                </h2>
                <ul className="space-y-2 text-white/70">
                  {coach.certifications.map((cert, idx) => (
                    <li key={idx} className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-premium-gold flex-shrink-0" />
                      <span>{cert}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Languages */}
            {coach.languages?.length > 0 && (
              <div className="bg-dark-surface rounded-xl p-6 border border-premium-gold/30/50">
                <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2" style={{ fontFamily: 'Fraunces, serif' }}>
                  <Languages className="w-6 h-6 text-premium-gold" />
                  Languages
                </h2>
                <div className="flex flex-wrap gap-2">
                  {coach.languages.map((lang, idx) => (
                    <span key={idx} className="px-3 py-1 bg-deep-dark text-white/70 text-sm rounded">
                      {lang}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Availability */}
            {coach.availability && Object.keys(coach.availability).length > 0 && (
              <div className="bg-dark-surface rounded-xl p-6 border border-premium-gold/30/50">
                <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2" style={{ fontFamily: 'Fraunces, serif' }}>
                  <Clock className="w-6 h-6 text-premium-gold" strokeWidth={1.5} />
                  Availability
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {Object.entries(coach.availability).map(([day, slots]) => (
                    <div key={day} className="flex justify-between text-white/70">
                      <span className="capitalize font-semibold">{day}:</span>
                      <span className="text-right">{slots.join(', ')}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Map */}
            <div className="bg-dark-surface rounded-xl p-6 border border-premium-gold/30/50">
              <h2 className="text-xl font-bold text-white mb-4 uppercase" style={{ fontFamily: 'Fraunces, serif' }}>
                Location
              </h2>
              <p className="text-white/60 mb-4 flex items-center gap-2">
                <MapPin className="w-5 h-5" strokeWidth={1.5} />
                {coach.city}, {coach.country}
                {coach.travel_radius_km && (
                  <span className="text-xs">(Willing to travel {coach.travel_radius_km}km)</span>
                )}
              </p>
              <div className="h-64 rounded-lg overflow-hidden">
                <MapContainer 
                  center={[coach.latitude, coach.longitude]} 
                  zoom={12} 
                  style={{ height: '100%', width: '100%' }}
                  scrollWheelZoom={false}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <Marker position={[coach.latitude, coach.longitude]}>
                    <Popup>{coach.name}</Popup>
                  </Marker>
                </MapContainer>
              </div>
            </div>

            {/* Contact */}
            <div className="bg-dark-surface rounded-xl p-6 border border-premium-gold/30/50">
              <h2 className="text-xl font-bold text-white mb-4 uppercase" style={{ fontFamily: 'Fraunces, serif' }}>
                Contact Information
              </h2>
              <div className="space-y-3">
                {coach.contact_phone && (
                  <div className="flex items-center gap-3 text-white/70">
                    <Phone className="w-5 h-5 text-premium-gold" strokeWidth={1.5} />
                    <a href={`tel:${coach.contact_phone}`} className="hover:text-white transition-colors">
                      {coach.contact_phone}
                    </a>
                  </div>
                )}
                {coach.contact_email && (
                  <div className="flex items-center gap-3 text-white/70">
                    <Mail className="w-5 h-5 text-premium-gold" strokeWidth={1.5} />
                    <a href={`mailto:${coach.contact_email}`} className="hover:text-white transition-colors">
                      {coach.contact_email}
                    </a>
                  </div>
                )}
                {coach.website && (
                  <div className="flex items-center gap-3 text-white/70">
                    <GlobeIcon className="w-5 h-5 text-premium-gold" />
                    <a href={coach.website} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                      Visit Website
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Booking Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-dark-surface rounded-xl p-6 border border-premium-gold/30/50 sticky top-4">
              <h3 className="text-2xl font-bold text-white mb-4 uppercase" style={{ fontFamily: 'Fraunces, serif' }}>
                Book Session
              </h3>
              
              {/* Pricing */}
              <div className="mb-6 space-y-2">
                <div className="flex justify-between text-white/70">
                  <span>Hourly Rate:</span>
                  <span className="text-white font-bold">₹{coach.hourly_rate}/hr</span>
                </div>
                {coach.session_packages?.map((pkg, idx) => (
                  <div key={idx} className="flex justify-between text-white/70 text-sm">
                    <span>{pkg.sessions} sessions:</span>
                    <span className="text-white font-bold">₹{pkg.price}</span>
                  </div>
                ))}
              </div>

              <form onSubmit={handleBookingSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-white/70 mb-2">Booking Type</label>
                  <select
                    value={bookingForm.booking_type}
                    onChange={(e) => setBookingForm({ ...bookingForm, booking_type: e.target.value })}
                    className="w-full px-3 py-2 bg-deep-dark border border-premium-gold/30 rounded-lg text-white focus:border-premium-gold focus:outline-none"
                  >
                    <option value="single_session">Single Session</option>
                    {coach.session_packages?.map((pkg, idx) => (
                      <option key={idx} value="package">{pkg.description}</option>
                    ))}
                  </select>
                </div>

                {bookingForm.booking_type === 'package' && (
                  <div>
                    <label className="block text-sm font-semibold text-white/70 mb-2">Select Package</label>
                    <select
                      value={bookingForm.package_id}
                      onChange={(e) => setBookingForm({ ...bookingForm, package_id: e.target.value })}
                      className="w-full px-3 py-2 bg-deep-dark border border-premium-gold/30 rounded-lg text-white focus:border-premium-gold focus:outline-none"
                      required
                    >
                      <option value="">Choose Package</option>
                      {coach.session_packages?.map((pkg, idx) => (
                        <option key={idx} value={pkg.id}>{pkg.description}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-semibold text-white/70 mb-2">Session Date & Time</label>
                  <input
                    type="datetime-local"
                    value={bookingForm.session_date}
                    onChange={(e) => setBookingForm({ ...bookingForm, session_date: e.target.value })}
                    className="w-full px-3 py-2 bg-deep-dark border border-premium-gold/30 rounded-lg text-white focus:border-premium-gold focus:outline-none"
                    required
                  />
                </div>

                {bookingForm.booking_type === 'single_session' && (
                  <div>
                    <label className="block text-sm font-semibold text-white/70 mb-2">Duration (hours)</label>
                    <input
                      type="number"
                      min="0.5"
                      step="0.5"
                      value={bookingForm.session_duration_hours}
                      onChange={(e) => setBookingForm({ ...bookingForm, session_duration_hours: parseFloat(e.target.value) })}
                      className="w-full px-3 py-2 bg-deep-dark border border-premium-gold/30 rounded-lg text-white focus:border-premium-gold focus:outline-none"
                      required
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-semibold text-white/70 mb-2">Sport</label>
                  <select
                    value={bookingForm.sport}
                    onChange={(e) => setBookingForm({ ...bookingForm, sport: e.target.value })}
                    className="w-full px-3 py-2 bg-deep-dark border border-premium-gold/30 rounded-lg text-white focus:border-premium-gold focus:outline-none"
                  >
                    {coach.specializations?.map((sport, idx) => (
                      <option key={idx} value={sport}>{sport}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-white/70 mb-2">Your Skill Level</label>
                  <select
                    value={bookingForm.skill_level}
                    onChange={(e) => setBookingForm({ ...bookingForm, skill_level: e.target.value })}
                    className="w-full px-3 py-2 bg-deep-dark border border-premium-gold/30 rounded-lg text-white focus:border-premium-gold focus:outline-none"
                  >
                    <option value="beginner">Beginner</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="advanced">Advanced</option>
                    <option value="professional">Professional</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-white/70 mb-2">Location Type</label>
                  <select
                    value={bookingForm.location_type}
                    onChange={(e) => setBookingForm({ ...bookingForm, location_type: e.target.value })}
                    className="w-full px-3 py-2 bg-deep-dark border border-premium-gold/30 rounded-lg text-white focus:border-premium-gold focus:outline-none"
                  >
                    {coach.in_person_coaching && <option value="in_person">In-Person</option>}
                    {coach.online_coaching && <option value="online">Online</option>}
                  </select>
                </div>

                {bookingForm.location_type === 'in_person' && (
                  <div>
                    <label className="block text-sm font-semibold text-white/70 mb-2">Location Address</label>
                    <input
                      type="text"
                      value={bookingForm.location_address}
                      onChange={(e) => setBookingForm({ ...bookingForm, location_address: e.target.value })}
                      placeholder="Enter location"
                      className="w-full px-3 py-2 bg-deep-dark border border-premium-gold/30 rounded-lg text-white placeholder-white/40 focus:border-premium-gold focus:outline-none"
                      required
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-semibold text-white/70 mb-2">Number of People</label>
                  <input
                    type="number"
                    min="1"
                    value={bookingForm.num_people}
                    onChange={(e) => setBookingForm({ ...bookingForm, num_people: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 bg-deep-dark border border-premium-gold/30 rounded-lg text-white focus:border-premium-gold focus:outline-none"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-white/70 mb-2">Special Requests</label>
                  <textarea
                    value={bookingForm.special_requests}
                    onChange={(e) => setBookingForm({ ...bookingForm, special_requests: e.target.value })}
                    className="w-full px-3 py-2 bg-deep-dark border border-premium-gold/30 rounded-lg text-white focus:border-premium-gold focus:outline-none"
                    rows="3"
                    placeholder="Any specific focus areas..."
                  />
                </div>

                {/* Total */}
                <div className="pt-4 border-t border-premium-gold/30/50">
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-white/70">Total Amount:</span>
                    <span className="text-2xl font-bold text-white" style={{ fontFamily: 'Fraunces, serif' }}>
                      ₹{calculatePrice().toLocaleString()}
                    </span>
                  </div>
                  
                  <button
                    type="submit"
                    className="w-full bg-premium-gold hover:bg-red-700 text-white font-bold tracking-wider py-3 rounded-lg transition-all duration-300 flex items-center justify-center gap-2"
                  >
                    Book Now <ArrowRight className="w-5 h-5" />
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CoachDetailPage;
